// src/components/GameCreation/Confirmation.js
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { previousPhase, resetPhase } from '../../store/slices/gamePhaseSlice';
import { resetSelectedStadium } from '../../store/slices/stadiumSelectionSlice';
import { resetSelectedDate } from '../../store/slices/dateSelectionSlice';
import { resetSelectedSlot } from '../../store/slices/slotSelectionSlice';
import { resetSelectedLevel } from '../../store/slices/levelSelectionSlice';
import { resetSelectedHost } from '../../store/slices/hostSelectionSlice'; // Import resetSelectedHost
import { formatCalendarDate } from '../../utils/calendarDate';
import './Confirmation.css';
import Button from '../../components/Button/Button';
import {
  useAlertDialog,
  useConfirmDialog,
} from '../../components/ConfirmDialog/ConfirmDialogProvider';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import CreateGameImageTwo from '../../assets/images/turia.png';

const normalizeId = (value) => (value ? String(value) : '');

const Confirmation = ({ gameData, mode = 'create', gameId = null, originalGameData = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showAlert = useAlertDialog();
  const confirm = useConfirmDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const selectedHost = useSelector((state) => state.hostSelection.selectedHost);

  const resetEditorState = () => {
    dispatch(resetSelectedStadium());
    dispatch(resetSelectedDate());
    dispatch(resetSelectedSlot());
    dispatch(resetSelectedLevel());
    dispatch(resetSelectedHost());
    dispatch(resetPhase());
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);
    const duration = (end - start) / (1000 * 60);
    return duration > 0 ? duration : 0;
  };

  const buildEditPatchPayload = (currentData) => {
    const original = originalGameData || {};
    const patchPayload = {};

    if (normalizeId(currentData.stadiumId) !== normalizeId(original.stadiumId)) {
      patchPayload.stadiumId = currentData.stadiumId;
    }

    if (normalizeId(currentData.hostId) !== normalizeId(original.hostId)) {
      patchPayload.hostId = currentData.hostId;
    }

    if (String(currentData.date || '') !== String(original.date || '')) {
      patchPayload.date = currentData.date;
    }

    if (String(currentData.time || '') !== String(original.time || '')) {
      patchPayload.time = currentData.time;
    }

    if (Number(currentData.duration || 0) !== Number(original.duration || 0)) {
      patchPayload.duration = currentData.duration;
    }

    if (String(currentData.type || '') !== String(original.type || '')) {
      patchPayload.type = currentData.type;
    }

    return patchPayload;
  };

  const handleConfirm = async () => {
    if (isSubmitting || isDeleting) {
      return;
    }

    if (!selectedHost) {
      await showAlert({
        title: 'GAME',
        message: 'No host selected.',
      });
      return;
    }

    if (!gameData?.stadium?._id || !gameData?.slot?.startTime || !gameData?.date || !gameData?.level) {
      await showAlert({
        title: 'GAME',
        message: 'Missing game data. Please complete all steps.',
      });
      return;
    }

    const duration = calculateDuration(gameData.slot.startTime, gameData.slot.endTime);

    const dataToSubmit = {
      stadiumId: normalizeId(gameData.stadium._id),
      hostId: normalizeId(selectedHost._id),
      date: gameData.date,
      time: gameData.slot.startTime,
      duration: duration,
      type: gameData.level,
    };

    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      await showAlert({
        title: 'GAME',
        message: 'You must be logged in to create a game.',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const requestConfig = {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };

      if (mode === 'edit') {
        if (!gameId) {
          await showAlert({
            title: 'GAME',
            message: 'Missing game ID for update.',
          });
          return;
        }

        const patchPayload = buildEditPatchPayload(dataToSubmit);

        if (Object.keys(patchPayload).length === 0) {
          resetEditorState();
          navigate(`/manage-game/${gameId}`);
          return;
        }

        await axios.patch(
          `${process.env.REACT_APP_API_URL}/games/${gameId}`,
          patchPayload,
          requestConfig
        );
        await showAlert({
          title: 'GAME',
          message: 'Game updated successfully!',
        });
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/games/create`,
          dataToSubmit,
          requestConfig
        );
        await showAlert({
          title: 'GAME',
          message: 'Game created successfully!',
        });
      }

      resetEditorState();

      navigate(mode === 'edit' ? `/manage-game/${gameId}` : "/dashboard");
    } catch (error) {
      console.error(mode === 'edit' ? "Error updating game:" : "Error creating game:", error);
      if (error.response && error.response.data) {
        await showAlert({
          title: 'GAME',
          message: `${mode === 'edit' ? 'Failed to update game' : 'Failed to create game'}: ${
            error.response.data.error || 'Unknown error'
          }`,
        });
      } else {
        await showAlert({
          title: 'GAME',
          message:
            mode === 'edit'
              ? 'Failed to update game. Please try again.'
              : 'Failed to create game. Please try again.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (mode !== 'edit' || !gameId || isDeleting) {
      return;
    }

    const shouldDelete = await confirm({
      title: 'DELETE GAME',
      message: 'Delete this game? This cannot be undone and registered players will be removed.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!shouldDelete) {
      return;
    }

    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      await showAlert({
        title: 'GAME',
        message: 'No token found. Please login again.',
      });
      return;
    }

    setIsDeleting(true);

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/games/${gameId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      resetEditorState();
      await showAlert({
        title: 'GAME',
        message: 'Game deleted successfully.',
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting game:', error);
      await showAlert({
        title: 'GAME',
        message: error?.response?.data?.error || 'Failed to delete game. Please try again.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="confirmation-layout">
      {(isSubmitting || isDeleting) && (
        <LoadingOverlay
          label={
            isDeleting
              ? 'Deleting game'
              : mode === 'edit'
                ? 'Updating game'
                : 'Creating game'
          }
        />
      )}
      <div className="confirmation-image-container">
        <img
          src={gameData.stadium?.image || CreateGameImageTwo}
          alt="Stadium"
          className="confirmation-image"
        />
      </div>
      <div className="confirmation-content">
        <h2>
          {gameData.slot
            ? `${gameData.slot.startTime} - ${gameData.slot.endTime}`
            : 'No Time Selected'}
        </h2>
        <p>
          {gameData.date
            ? formatCalendarDate(gameData.date, 'en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })
            : 'No Date Selected'}
        </p>
        <h1>{gameData.stadium?.name || 'Unknown Stadium'}</h1>
        <p>{gameData.level || 'No Level Selected'}</p>
        <p>
          Host: {selectedHost ? `${selectedHost.first_name} ${selectedHost.last_name}` : 'No Host Selected'}
        </p>
        <p>
          {gameData.stadium?.address || 'Unknown Address'}
          <br />
          Capacity: {gameData.stadium?.capacity || 'Unknown'}
        </p>
        <p>
          Team Size:{' '}
          {gameData.stadium ? Math.floor(gameData.stadium.capacity / 2) : 'Unknown'}
        </p>
        <div className="confirmation-buttons">
          <Button
            text="Go Back"
            onClick={() => dispatch(previousPhase())}
            disabled={isSubmitting || isDeleting}
          />
          <Button
            text={
              isSubmitting
                ? mode === 'edit'
                  ? 'Updating...'
                  : 'Creating...'
                : 'Confirm'
            }
            onClick={handleConfirm}
            disabled={isSubmitting || isDeleting}
          />
        </div>
        {mode === 'edit' && (
          <button
            type="button"
            className="confirmation-delete-link"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete game'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Confirmation;
